import React, { useState, useEffect, useRef } from 'react';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import { ApiService } from 'services';

const PAGE_SIZE = 10;
const formatDate = date => moment(date).format('MMM DD, YYYY');

// Wraps the statement banner. Clicking it opens a list of the published
// statements for the account, newest first, with the draft at the top.
const StatementVersionPicker = ({ accountId, selectedVersion, showDraft, onSelect, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [versions, setVersions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef(null);

  // Reload the list when the account changes
  useEffect(() => {
    setVersions(null);
  }, [accountId]);

  // Close when clicking outside
  useEffect(() => {
    const onDocumentClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, []);

  const loadAllVersions = async () => {
    setIsLoading(true);
    try {
      let all = [];
      let page = 1;
      let total = 0;
      do {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await ApiService.getStatementVersions(accountId, page);
        const pageItems = (data && data.versions) || [];
        total = (data && data.totalCount) || 0;
        all = all.concat(pageItems);
        page += 1;
        if (pageItems.length === 0) {
          break;
        }
      } while (all.length < total && page < 1000 / PAGE_SIZE);
      all.sort((a, b) => moment(b.effectiveFrom).valueOf() - moment(a.effectiveFrom).valueOf());
      setVersions(all);
    } catch (err) {
      setVersions([]);
    }
    setIsLoading(false);
  };

  const toggle = () => {
    const willOpen = !isOpen;
    setIsOpen(willOpen);
    if (willOpen && versions === null && !isLoading) {
      loadAllVersions();
    }
  };

  const isDraftSelected = selectedVersion === null || typeof selectedVersion === 'undefined';

  return (
    <div className="statement-version-picker" ref={ containerRef }>
      <div
        className="statement-version-picker-trigger"
        role="button"
        tabIndex={ 0 }
        aria-haspopup="listbox"
        aria-expanded={ isOpen }
        title="Click to choose a statement version"
        onClick={ toggle }
        onKeyDown={ (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } } }
      >
        { children }
      </div>
      { isOpen && <ul className="statement-version-picker-list" role="listbox">
        { showDraft && <li
          role="option"
          aria-selected={ isDraftSelected }
          className={ `statement-version-picker-item${ isDraftSelected ? ' selected' : '' }` }
          tabIndex={ 0 }
          onClick={ () => { setIsOpen(false); onSelect(null); } }
          onKeyDown={ (e) => { if (e.key === 'Enter') { setIsOpen(false); onSelect(null); } } }
        >
          <strong>Draft</strong>
        </li> }
        { isLoading && <li className="statement-version-picker-message">Loading published statements...</li> }
        { !isLoading && versions && versions.length === 0 && <li className="statement-version-picker-message">No published statements</li> }
        { !isLoading && versions && versions.map((version, index) => {
          const isSelected = !isDraftSelected && String(selectedVersion) === String(version.version);
          return (
            <li
              key={ `version_${ version.version }` }
              role="option"
              aria-selected={ isSelected }
              className={ `statement-version-picker-item${ isSelected ? ' selected' : '' }` }
              tabIndex={ 0 }
              onClick={ () => { setIsOpen(false); onSelect(version.version); } }
              onKeyDown={ (e) => { if (e.key === 'Enter') { setIsOpen(false); onSelect(version.version); } } }
            >
              <strong>{ (version.data && version.data.name) || `Version ${ version.version }` }</strong>
              <span className="statement-version-picker-dates">
                { `Effective from ${ formatDate(version.effectiveFrom) } to ${ version.effectiveTo ? formatDate(version.effectiveTo) : 'no expiry' }` }
                { index === 0 && ' (Latest)' }
              </span>
            </li>
          );
        }) }
      </ul> }
    </div>
  );
};

StatementVersionPicker.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  selectedVersion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showDraft: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired
};

StatementVersionPicker.defaultProps = {
  selectedVersion: null,
  showDraft: true
};

export default StatementVersionPicker;
